// Types for the editorial-side /electionday turnout state.
//
// Persisted in Postgres (table `electionday_state`, row kind='turnout',
// payload jsonb) — deliberately separate from the editorial result
// payload so the 2pm/10pm turnout-entry flow and the per-district
// vote-count entry flow can't clobber each other when they save
// concurrently on counting day (both use full-document-replace PUTs).
//
// TurnoutTileGrid expects two distinct values per district —
// pct_2pm AND pct_10pm — so the day's arc renders as two rules on
// the vertical bar rather than collapsing to one value. The shape
// here matches that.
//
// The vs-2022 delta is derived at render time against
// malta_districts_2022.geojson's results_2022.turnout_pct — never
// entered by the editor.

export const TURNOUT_DISTRICT_KEYS: readonly string[] = [
  'District 1',
  'District 2',
  'District 3',
  'District 4',
  'District 5',
  'District 6',
  'District 7',
  'District 8',
  'District 9',
  'District 10',
  'District 11',
  'District 12',
  'District 13',
];

export interface NationalTurnout {
  /** National turnout at the ~14:00 Electoral Commission reading, 0–100. */
  pct_2pm: number | null;
  /** National turnout at the ~22:00 closing reading, 0–100. */
  pct_10pm: number | null;
  /** Total ballots cast at the most recent reading. Optional context. */
  ballotsCast: number | null;
  /** Total eligible voters. Optional — for sanity checks. */
  registered: number | null;
}

export interface DistrictTurnout {
  /** 2pm reading for this district, 0–100. Drives the 2pm rule on
   *  TurnoutTileGrid's vertical bar. Null until entered. */
  pct_2pm: number | null;
  /** 10pm closing reading, 0–100. Drives the 10pm rule + flips the
   *  vertical-bar fill from baseline to live. Null until entered. */
  pct_10pm: number | null;
  /** Optional absolute ballot counts at each reading. */
  ballotsCast_2pm: number | null;
  ballotsCast_10pm: number | null;
}

export interface TurnoutState {
  /** Editor-set free-text label — e.g. "As of 14:00", "Final 22:00". */
  label: string | null;
  national: NationalTurnout;
  /** Keyed by district label ("District 1"…"District 13"). Missing
   *  districts simply have no turnout reading yet. */
  districts: Record<string, DistrictTurnout>;
  /** Optional editorial note shown beneath the figures. */
  note: string | null;
  /** Last-touched timestamp, server-set. */
  updatedAt: string;
}

/** Default empty state for fresh fetches / fallback rendering. */
export function emptyTurnoutState(): TurnoutState {
  return {
    label: null,
    national: {
      pct_2pm: null,
      pct_10pm: null,
      ballotsCast: null,
      registered: null,
    },
    districts: {},
    note: null,
    updatedAt: new Date(0).toISOString(),
  };
}

/** True when at least the national pct_2pm or pct_10pm has been
 *  entered — used by the homepage section + hero headline to gate
 *  visibility ("appear only when figures exist"). */
export function hasTurnoutData(state: TurnoutState | null | undefined): boolean {
  if (!state) return false;
  return (
    state.national.pct_2pm !== null || state.national.pct_10pm !== null
  );
}
